import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './styles/tw.css';
import './styles/globals.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css';
import App from './App';
import { installClientErrorLogging, reportClientError } from './utils/clientErrorLogger';
import { initSentry } from './utils/sentry';

initSentry();
installClientErrorLogging();

// Dependency-free fallback (inline styles) shown if a React render crash bubbles to the top —
// won't rely on Tailwind/CSS having loaded. Sentry reports the error when configured.
const errorFallback = (
  <div style={{ padding: '2rem', maxWidth: 420, margin: '15vh auto 0', textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
    <h1 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Something went wrong</h1>
    <p style={{ color: '#555', marginBottom: '1.25rem' }}>Please reload the app. If it keeps happening, let us know.</p>
    <button
      onClick={() => window.location.reload()}
      style={{ padding: '10px 20px', borderRadius: 9999, border: 'none', background: '#AFBB8B', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
    >
      Reload
    </button>
  </div>
);

// The boundary swallows the crash, so it never reaches window.onerror and the clientErrorLogger
// listeners never see it. Forward it explicitly so the error still lands in the backend log even
// when Sentry has no DSN configured. Sentry calls onError regardless of whether it initialised.
const handleBoundaryError = (error, componentStack, eventId) => {
  reportClientError('react.errorboundary', error, {
    componentStack: typeof componentStack === 'string' ? componentStack.slice(0, 4000) : '',
    sentryEventId: eventId || '',
  });
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={errorFallback} onError={handleBoundaryError}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// Register the service worker for PWA installability + offline app shell. Production only —
// in dev it would cache dev bundles and cause stale-load confusion. Test locally via a prod
// build (`npm run build && npx serve -s build`) or on a deploy.
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('Service worker registration failed:', err);
    });
  });
}
